import { Platform } from 'react-native';
import type { GenerateChecklistPdfParams } from './checklistPdfDoc';
import type { PartnerChecklistReceipt } from './partnerChecklistService';
import { observationKey } from './checklist/checklistObservations';

export function getPartnerReceiptPdfErrorMessage(error: unknown): string | null {
  const code = (error as { code?: string })?.code;
  if (['partner_receipt_media_unavailable', 'partner_media_read_failed', 'partner_background_read_failed'].includes(code ?? '')) {
    return 'Uma imagem compartilhada está indisponível ou foi removida. Tente novamente ou solicite o PDF à empresa.';
  }
  return null;
}

/** Adapta exclusivamente o retrato público confirmado, nunca o rascunho ou catálogo administrativo. */
export function partnerReceiptPdfParams(receipt: PartnerChecklistReceipt): GenerateChecklistPdfParams {
  if (receipt.receipt !== true || receipt.share_link.status !== 'completed' ||
      !receipt.checklist.id || !receipt.empresa?.id) throw new Error('Comprovante indisponível para este link.');
  const fields = receipt.fields.filter(field => field.permission === 'read' || field.permission === 'edit');
  const values: Record<string, string | null> = {};
  const mediaMetadata: NonNullable<GenerateChecklistPdfParams['publicSnapshot']>['mediaMetadata'] = {};
  for (const field of fields) {
    values[field.id] = receipt.values[field.id] ?? null;
    values[observationKey(field.id)] = receipt.observations?.[field.id] ?? null;
    const metadata = receipt.media_metadata?.[field.id];
    if (metadata && ['foto', 'photo', 'assinatura', 'signature', 'marcação', 'marcacao', 'mark'].includes(field.tipo)) {
      mediaMetadata[field.id] = { created_at: metadata.created_at, updated_at: metadata.updated_at };
    }
  }
  return {
    checklist: {
      id: receipt.checklist.id, template_id: receipt.checklist.template_id ?? '',
      empresa_id: receipt.empresa.id, status: receipt.checklist.status ?? 'in_progress',
      sequencial_codigo: receipt.checklist.sequencial_codigo, sequencial_numero: receipt.checklist.sequencial_numero,
      data_criacao: receipt.checklist.data_criacao, data_conclusao: receipt.checklist.data_conclusao,
    },
    template: fields.map(field => ({ id: field.id, nome: field.nome, tipo: field.tipo, grupo: field.grupo, ordem: field.ordem })),
    values,
    publicSnapshot: { company: {
      id: receipt.empresa.id, nome: receipt.empresa.nome, cnpj: receipt.empresa.cnpj,
      telefone: receipt.empresa.telefone, email: receipt.empresa.email, endereco: receipt.empresa.endereco,
      cidade: receipt.empresa.cidade, uf: receipt.empresa.uf, cep: receipt.empresa.cep, logo_url: receipt.empresa.logo_url,
    }, fieldIds: fields.map(field => field.id), mediaMetadata },
  };
}

export async function generatePartnerReceiptPdf(receipt: PartnerChecklistReceipt): Promise<void> {
  const params = partnerReceiptPdfParams(receipt);
  if (Platform.OS === 'web') {
    const { generateChecklistPdfWeb } = await import('./checklistPdfService.web');
    await generateChecklistPdfWeb(params);
  } else {
    const { generateChecklistPdfNative } = await import('./checklistPdfService.native');
    await generateChecklistPdfNative(params);
  }
}
